import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CalendarClock, CheckCircle2, Swords, Wind, Hand, Flame, X, ClipboardList, Pencil } from "lucide-react";

const WORK_ICON = { "Hard Sparring": Swords, "Technical/Flow Sparring": Wind, "Padwork/Clinch": Hand };

function fmtWhen(s) {
  if (!s) return "Time TBC";
  const d = new Date(s);
  if (isNaN(d)) return "Time TBC";
  return d.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function LogModal({ session, open, onOpenChange, onLogged }) {
  const [rounds, setRounds] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    const r = parseInt(rounds, 10);
    if (!r || r < 1) { toast.error("Enter the number of rounds."); return; }
    setLoading(true);
    try {
      await api.post(`/sessions/${session.id}/log`, { rounds: r, note });
      toast.success("Session logged.");
      setRounds(""); setNote("");
      onLogged();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  if (!session) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="log-session-modal" className="bg-[#161616] border-[#27272a] text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase tracking-tight">Log Session</DialogTitle>
          <DialogDescription className="text-zinc-400">{session.a_name} vs {session.b_name} · {session.work_type}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-wider text-zinc-500">Rounds completed</label>
          <Input data-testid="log-rounds" type="number" value={rounds} onChange={(e) => setRounds(e.target.value)} placeholder="e.g. 6" className="bg-[#0d0d0d] border-[#27272a] text-white h-11 focus:ring-2 focus:ring-volt" />
        </div>
        <Textarea data-testid="log-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Short note (optional) — how it went, focus areas…" className="bg-[#0d0d0d] border-[#27272a] text-white focus:ring-2 focus:ring-volt resize-none" />
        <button data-testid="log-submit-btn" onClick={submit} disabled={loading} className="w-full bg-volt text-black font-heading font-bold uppercase tracking-widest py-3 rounded-xl hover:bg-volt-hover transition-colors duration-200 disabled:opacity-40">
          {loading ? "Saving…" : "Save to Log"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function RescheduleModal({ session, open, onOpenChange, onSaved }) {
  const [when, setWhen] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!when) { toast.error("Pick a new date & time."); return; }
    setLoading(true);
    try {
      await api.patch(`/sessions/${session.id}/schedule`, { scheduled_at: when });
      toast.success("Session rescheduled.");
      setWhen("");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  if (!session) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reschedule-modal" className="bg-[#161616] border-[#27272a] text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase tracking-tight">Reschedule</DialogTitle>
          <DialogDescription className="text-zinc-400">{session.a_name} vs {session.b_name} · {session.work_type}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-wider text-zinc-500">New date &amp; time</label>
          <input data-testid="reschedule-input" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="w-full bg-[#0d0d0d] border border-[#27272a] text-white rounded-lg h-11 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-volt [color-scheme:dark]" />
        </div>
        <button data-testid="reschedule-submit-btn" onClick={submit} disabled={loading} className="w-full bg-volt text-black font-heading font-bold uppercase tracking-widest py-3 rounded-xl hover:bg-volt-hover transition-colors duration-200 disabled:opacity-40">
          {loading ? "Saving…" : "Update Time"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

export default function SessionsView() {
  const qc = useQueryClient();
  const [logTarget, setLogTarget] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [reschedTarget, setReschedTarget] = useState(null);
  const [reschedOpen, setReschedOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["sessions"],
    queryFn: async () => (await api.get("/sessions")).data,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["sessions"] });

  const cancel = async (id) => {
    try { await api.delete(`/sessions/${id}`); toast.success("Session removed."); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openLog = (s) => { setLogTarget(s); setLogOpen(true); };
  const openResched = (s) => { setReschedTarget(s); setReschedOpen(true); };

  if (isLoading) return <div className="text-center py-20 text-volt font-heading uppercase tracking-widest animate-pulse">Loading…</div>;

  const upcoming = data?.upcoming || [];
  const history = data?.history || [];

  const Card = ({ s, done }) => {
    const WI = WORK_ICON[s.work_type] || Swords;
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid={`session-${s.id}`} className="flex items-center gap-3 p-4 bg-card border border-white/10 rounded-xl">
        <div className="w-10 h-10 rounded-lg bg-[#0d0d0d] border border-[#27272a] flex items-center justify-center shrink-0">
          <WI size={18} className="text-volt" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading uppercase tracking-wide truncate">{s.a_name} <span className="text-zinc-600">vs</span> {s.b_name}</div>
          <div className="text-xs text-zinc-400 truncate flex items-center gap-1.5">
            {s.work_type}
            {s.camp_name && <span className="inline-flex items-center gap-1 text-volt"><Flame size={10} />{s.camp_name}</span>}
          </div>
          {done ? (
            <div className="text-xs text-zinc-500 mt-0.5">{s.rounds} rounds{s.note ? ` · “${s.note}”` : ""}</div>
          ) : (
            <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1"><CalendarClock size={11} className="text-volt" />{fmtWhen(s.scheduled_at)}</div>
          )}
        </div>
        {done ? (
          <span className="shrink-0 text-[10px] uppercase tracking-widest text-volt border border-volt/40 px-2 py-1 rounded-lg flex items-center gap-1"><CheckCircle2 size={11} />Logged</span>
        ) : (
          <div className="flex gap-2 shrink-0">
            <button data-testid={`reschedule-session-${s.id}`} onClick={() => openResched(s)} className="w-9 h-9 flex items-center justify-center border border-[#27272a] text-zinc-400 rounded-lg hover:text-volt hover:border-volt transition-colors duration-200" title="Reschedule"><Pencil size={15} /></button>
            <button data-testid={`log-session-${s.id}`} onClick={() => openLog(s)} className="bg-volt text-black text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-lg hover:bg-volt-hover transition-colors duration-200">Log rounds</button>
            <button data-testid={`cancel-session-${s.id}`} onClick={() => cancel(s.id)} className="w-9 h-9 flex items-center justify-center border border-[#27272a] text-zinc-400 rounded-lg hover:text-[#FF3B30] hover:border-[#FF3B30] transition-colors duration-200"><X size={16} /></button>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="space-y-8" data-testid="sessions-view">
      <div>
        <h3 className="font-heading text-2xl uppercase tracking-tight mb-3 flex items-center gap-2"><CalendarClock size={20} className="text-volt" /> Upcoming Sessions</h3>
        <div className="space-y-3">
          {upcoming.length ? upcoming.map((s) => <Card key={s.id} s={s} />) : (
            <div className="flex flex-col items-center justify-center py-10 text-zinc-600 border border-dashed border-[#27272a] rounded-2xl">
              <CalendarClock size={26} className="mb-2" />
              <p className="text-sm uppercase tracking-wider">No sessions scheduled</p>
              <p className="text-xs mt-1">Propose a time when you request rounds to line one up.</p>
            </div>
          )}
        </div>
      </div>
      <div>
        <h3 className="font-heading text-2xl uppercase tracking-tight mb-3 flex items-center gap-2"><ClipboardList size={20} className="text-volt" /> Sparring Log</h3>
        <div className="space-y-3">
          {history.length ? history.map((s) => <Card key={s.id} s={s} done />) : (
            <p className="text-sm text-zinc-600">No completed sessions yet. Log a session to build your history.</p>
          )}
        </div>
      </div>
      <LogModal session={logTarget} open={logOpen} onOpenChange={setLogOpen} onLogged={refresh} />
      <RescheduleModal session={reschedTarget} open={reschedOpen} onOpenChange={setReschedOpen} onSaved={refresh} />
    </div>
  );
}
